import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useSlider } from '../../contexts/SliderContext';
import { getOptimizedImageUrl, supportsWebP } from '../../utils/imageOptimization';
import { resolveMediaUrl } from '../../utils/mediaUrl';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HeroSlider = () => {
  const { t } = useTranslation();
  const { countryCode } = useLocalization();
  const { setCurrentSliderTextColor } = useSlider();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (countryCode) {
      fetchSliders();
    }
  }, [countryCode]);

  const fetchSliders = async () => {
    try {
      const response = await axios.get(`${API}/sliders`, {
        params: { country: countryCode }
      });
      setSlides(response.data);
    } catch (error) {
      console.error('Error fetching sliders:', error);
      // Fallback to default slides if API fails
      setSlides([
        {
          id: '1',
          heading: t('hero.heading1'),
          subheading: t('hero.heading2') + ' ' + t('hero.brandName'),
          buttonText: t('hero.cta'),
          buttonLink: '/shop',
          image: 'https://placehold.co/1920x1080/E8E4DE/2C2420?text=Hero+1',
          duration: 5000
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get optimized image URL for hero slider
  const getImageUrl = (imagePath) => {
    if (!imagePath) return '';

    // If it's an object with optimized versions, use them
    if (typeof imagePath === 'object' && imagePath.optimized) {
      return getOptimizedImageUrl(imagePath, 'large', supportsWebP());
    }

    // Delegate to the canonical resolver (handles s3/cdn rewrite of legacy paths)
    return resolveMediaUrl(typeof imagePath === 'string' ? imagePath : '');
  };

  // Auto-advance slider with individual slide durations
  useEffect(() => {
    if (slides.length === 0) return;
    
    const currentSlideDuration = slides[currentSlide]?.duration || 5000;
    
    const timer = setTimeout(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, currentSlideDuration);
    
    return () => clearTimeout(timer);
  }, [slides, currentSlide]);

  // Update text color when slide changes
  useEffect(() => {
    if (slides.length > 0 && slides[currentSlide]) {
      const textColor = slides[currentSlide].textColor || 'white';
      setCurrentSliderTextColor(textColor);
    }
  }, [currentSlide, slides, setCurrentSliderTextColor]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const scrollToContent = () => {
    window.scrollTo({
      top: window.innerHeight,
      behavior: 'smooth'
    });
  };

  if (loading) {
    return (
      <div
        className="relative w-full h-screen flex items-center justify-center"
        style={{ background: 'var(--bg)' }}
        data-testid="hero-loader"
      >
        <span className="rb-loader" aria-label={t('heroSlider.loading')} role="status" />
        <style>{`
          .rb-loader {
            width: 8px;
            height: 8px;
            box-shadow: 0 15px, 0 -15px;
            border-radius: 2px;
            background: currentColor;
            display: block;
            margin: -25px auto 0;
            position: relative;
            color: var(--terracotta);
            transform: translateY(15px);
            box-sizing: border-box;
            animation: rb-animloader 2s ease infinite;
          }
          .rb-loader::after,
          .rb-loader::before {
            content: '';
            box-sizing: border-box;
            width: 8px;
            height: 8px;
            box-shadow: 0 15px, 0 -15px;
            border-radius: 2px;
            background: currentColor;
            color: var(--terracotta);
            position: absolute;
            left: 15px;
            top: 0;
            animation: rb-animloader 2s 0.2s ease infinite;
          }
          .rb-loader::before {
            animation-delay: 0.4s;
            left: 30px;
          }
          @keyframes rb-animloader {
            0%   { top: 0;    color: var(--terracotta); }
            50%  { top: 15px; color: rgba(224, 122, 95, 0.25); }
            100% { top: 0;    color: var(--terracotta); }
          }
        `}</style>
      </div>
    );
  }

  if (slides.length === 0) {
    return null;
  }

  return (
    <div className="relative w-full h-screen overflow-hidden">
      {/* Slides */}
      {slides.map((slide, index) => (
        <div
          key={slide.id || slide.slug || `slide-${index}`}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentSlide ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {/* Background Image - Full viewport height */}
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ 
              backgroundImage: `url(${getImageUrl(slide.image)})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          >
            {/* Editorial overlay for depth + readability */}
            <div
              className="absolute inset-0"
              style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.45) 100%)' }}
            />
          </div>

          {/* Content - Positioned at bottom left */}
          <div className="relative h-full container mx-auto px-6 md:px-12 lg:px-16 flex items-end pb-24 md:pb-32">
            <div className="max-w-3xl text-left space-y-6 animate-fadeIn">
              {/* Main Heading — Fraunces editorial */}
              <h1
                style={{
                  fontFamily: 'var(--font-display)',
                  fontVariationSettings: "'opsz' 144, 'SOFT' 30",
                  fontSize: 'clamp(56px, 11vw, 160px)',
                  fontWeight: 300,
                  lineHeight: 0.92,
                  letterSpacing: '-3px',
                  color: '#fefcf7',
                  textShadow: '0 4px 24px rgba(0,0,0,0.4)',
                }}
              >
                {slide.heading}
              </h1>
              
              {/* Subheading */}
              {slide.subheading && (
                <p
                  style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: 'clamp(15px, 1.5vw, 18px)',
                    fontWeight: 300,
                    letterSpacing: '0.3px',
                    color: 'rgba(255,255,255,0.88)',
                    marginTop: '20px',
                    maxWidth: '40rem',
                    lineHeight: 1.6,
                  }}
                >
                  {slide.subheading}
                </p>
              )}
              
              {/* CTA Button — outline editorial (no saturated orange) */}
              {slide.buttonText && (
                <div className="pt-4">
                  <Link to={slide.buttonLink || '/shop'}>
                    <button className="btn-outline-light" type="button">
                      {slide.buttonText}
                    </button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}

      {/* Navigation arrows — thin editorial chevrons, bottom-right cluster */}
      {slides.length > 1 && (
        <div
          className="absolute z-20 flex items-center gap-2"
          style={{ right: '32px', bottom: '40px' }}
        >
          <button
            onClick={prevSlide}
            aria-label={t('heroSlider.previousSlide')}
            type="button"
            className="flex items-center justify-center transition-all"
            style={{
              width: '44px',
              height: '44px',
              border: '1px solid rgba(255,255,255,0.5)',
              background: 'transparent',
              color: '#fefcf7',
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#fefcf7';
              e.currentTarget.style.color = 'var(--ink)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = '#fefcf7';
            }}
          >
            <ChevronLeft strokeWidth={1.2} style={{ width: 18, height: 18 }} />
          </button>
          <button
            onClick={nextSlide}
            aria-label={t('heroSlider.nextSlide')}
            type="button"
            className="flex items-center justify-center transition-all"
            style={{
              width: '44px',
              height: '44px',
              border: '1px solid rgba(255,255,255,0.5)',
              background: 'transparent',
              color: '#fefcf7',
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#fefcf7';
              e.currentTarget.style.color = 'var(--ink)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = '#fefcf7';
            }}
          >
            <ChevronRight strokeWidth={1.2} style={{ width: 18, height: 18 }} />
          </button>
        </div>
      )}

      {/* Slide Indicators - Minimal design */}
      {slides.length > 1 && (
        <div className="absolute bottom-10 left-6 md:left-12 lg:left-16 flex gap-3 z-20">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`h-0.5 rounded-full transition-all duration-500 ${
                index === currentSlide
                  ? 'w-16 bg-white'
                  : 'w-8 bg-white/40 hover:bg-white/70'
              }`}
              aria-label={t('heroSlider.goToSlide', { number: index + 1 })}
            />
          ))}
        </div>
      )}

      {/* Scroll Down Indicator */}
      <button
        onClick={scrollToContent}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2 text-white animate-bounce cursor-pointer group"
        aria-label={t('hero.scrollDown')}
      >
        <span className="text-sm uppercase tracking-widest font-light opacity-80 group-hover:opacity-100 transition-opacity">
          {t('hero.scroll')}
        </span>
        <ChevronDown className="h-6 w-6 opacity-80 group-hover:opacity-100 transition-opacity" strokeWidth={1.5} />
      </button>
    </div>
  );
};

export default HeroSlider;